/**
 * FIN-RPT-FALSE-EMPTY-1 — the pure half of the report-state contract.
 *
 * Kept free of React so the query client can share `isPermissionError` without
 * pulling components into the entry bundle. `ReportStates.tsx` re-exports both
 * for the pages.
 *
 * The rule: a financial report may say "no data" ONLY after a successful fetch
 * that returned none. Every other situation — denied, failed, loading, retrying,
 * never-asked — has its own state and its own sentence.
 *
 * The full contract, in one place:
 *
 *   disabled / prerequisite missing (no company yet)   -> idle        (never empty)
 *   paused, nothing ever attempted                     -> offline     (never idle)
 *   paused, a failed attempt behind it                 -> error       (never offline)
 *   first fetch in flight                              -> loading
 *   failed once, retrying, still pending               -> loading     (never idle)
 *   401 / 403 / 42501 / P0001                          -> denied
 *   any other failure                                  -> error       (retryable)
 *   succeeded, rows present, refetching                -> refreshing  (rows stay)
 *   succeeded, rows present                            -> data
 *   succeeded, no rows                                 -> empty
 *
 * EMPTY is reachable from exactly one branch: a query that has successfully
 * completed. Nothing else can produce it.
 */

export type ReportQueryLike = {
  isPending?: boolean;
  /** React Query pauses a query when it believes the network is unavailable. */
  fetchStatus?: 'fetching' | 'paused' | 'idle';
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  isSuccess?: boolean;
  error?: unknown;
  /** React Query surfaces the in-flight failure before `isError` flips. */
  failureCount?: number;
  failureReason?: unknown;
  /**
   * FIN-RPT-PAUSED-2 — how many times this query has *reported* an error.
   *
   * The only failure evidence that survives a refetch. Starting a fetch runs
   * React Query's `fetchState()`, which resets `failureCount` to 0, clears
   * `failureReason` and `error`, and puts `status` back to `pending` when there
   * is no data — so after a refetch a query that has failed twice is byte for
   * byte a query that has never been asked, except for this counter.
   */
  errorUpdateCount?: number;
  refetch?: () => unknown;
};

/**
 * What the *browser* says about the network, supplied by the caller.
 *
 * Deliberately not React Query's `onlineManager`: that manager seeds itself
 * `true` and thereafter moves only on `window` 'online'/'offline' events — it
 * never reads `navigator.onLine` — so the two can disagree indefinitely, and on
 * Production they did.
 */
export type ReportEnv = {
  /** `navigator.onLine`. Omitted means "unknown", which is treated as online. */
  online?: boolean;
};

export type ReportState = 'loading' | 'refreshing' | 'denied' | 'error' | 'offline' | 'idle' | 'empty' | 'data';

/** Authorization failures are their own state: the report exists, this reader may not see it. */
export function isPermissionError(error: unknown): boolean {
  if (!error) return false;
  const parts: string[] = [];
  const e = error as { message?: unknown; code?: unknown; status?: unknown; cause?: unknown; details?: unknown };
  for (const v of [e.message, e.code, e.status, e.details]) if (v != null) parts.push(String(v));
  if (e.cause) {
    const c = e.cause as { message?: unknown; code?: unknown; status?: unknown };
    for (const v of [c.message, c.code, c.status]) if (v != null) parts.push(String(v));
  }
  const blob = parts.join(' ').toLowerCase();
  // Only authorization signals: SQLSTATE 42501, HTTP 401/403, and the explicit phrases the
  // RPC gates raise. A bare P0001 or the word "insufficient" on its own is a business-rule
  // failure (insufficient stock, insufficient balance) and must read as an error, not as
  // "you may not see this" (WIRING audit 2026-09-06).
  return /access denied|permission denied|not authori[sz]ed|unauthori[sz]ed|insufficient (?:permission|privilege|role|authority)|42501|\b401\b|\b403\b/.test(blob);
}

/**
 * Classifies what a report should render. `rowCount` is consulted only once the
 * query has actually succeeded.
 *
 * Two orderings in here are load-bearing:
 *
 *  - `isSuccess` is answered before any loading branch, so a report that already
 *    has rows never regresses to a spinner (or, worse, to "no data") while it
 *    refetches. A refetch over existing rows is `refreshing`: the table stays,
 *    with an indicator above it.
 *  - `failureCount` is consulted before the idle branch. While React Query waits
 *    between retries a failing query is pending-but-not-fetching, which is
 *    indistinguishable from a query that was never asked. Measured on Production,
 *    that made a denied report sit on "select a company" instead of naming the
 *    authorization failure.
 *
 * A refetch over a *successful but empty* result stays `empty`: that is the last
 * answer the server actually gave, and the refreshing indicator says an update is
 * in flight. It is never "empty" on the strength of a fetch that did not happen.
 */
export function classifyReportState(q: ReportQueryLike, rowCount: number, env: ReportEnv = {}): ReportState {
  if (q.isError) return isPermissionError(q.error) ? 'denied' : 'error';
  // A permission failure is never transient — surface it without waiting for
  // retries to exhaust.
  if (q.failureReason && isPermissionError(q.failureReason)) return 'denied';
  if (q.isSuccess) {
    if (rowCount === 0) return 'empty';
    return q.isFetching ? 'refreshing' : 'data';
  }
  if (q.fetchStatus === 'paused') {
    // The browser itself says there is no network. That sentence is true no
    // matter what else has happened, so it wins.
    if (env.online === false) return 'offline';
    // FIN-RPT-PAUSED-2 — paused is NOT a synonym for offline. React Query also
    // pauses a retry whose document is hidden, and a refetch that begins while
    // its `onlineManager` believes the network is gone re-enters `paused` with
    // the failure counters already wiped. Measured on Production: a forced HTTP
    // 500 on `get_subledger_gl_reconciliation` left the query pending / paused /
    // failureCount 0 with `navigator.onLine` true, and the page said
    // "no network connection" about a server error.
    //
    // A pause with a failed attempt behind it is a FAILURE, and the contract for
    // any non-authorization failure is the retryable error.
    if (hasFailedAttempt(q)) return 'error';
    // Nothing was ever sent: React Query has not asked because it believes there
    // is no network. Pending and not fetching — indistinguishable from "never
    // asked" unless fetchStatus is consulted. Measured on Production, that made a
    // paused report sit on "select a company and period" while nothing was in
    // flight.
    return 'offline';
  }
  if (q.isLoading || (q.isPending && q.isFetching)) return 'loading';
  // Failed at least once and still pending: retrying, not idle.
  if ((q.failureCount ?? 0) > 0) return 'loading';
  // Pending with no attempt behind it: disabled, or waiting on a prerequisite
  // such as the active company. Never an empty report.
  return 'idle';
}

/**
 * Whether an attempt to load this report has already failed.
 *
 * Four independent traces, because React Query keeps different ones alive at
 * different points: `failureCount`/`failureReason` while a retry is pending,
 * `error` once it is reported, and `errorUpdateCount` — the only one that
 * survives the next fetch starting.
 */
export function hasFailedAttempt(q: ReportQueryLike): boolean {
  return (q.failureCount ?? 0) > 0
    || q.failureReason != null
    || (q.errorUpdateCount ?? 0) > 0
    || q.error != null;
}

/**
 * `navigator.onLine`, or `undefined` where there is no navigator at all.
 *
 * Read here rather than inside the classifier so the state machine stays a pure
 * function of the values handed to it.
 */
export function browserOnline(): boolean | undefined {
  return typeof navigator === 'undefined' ? undefined : navigator.onLine;
}

/** True while a report that already has an answer is fetching a fresh one. */
export function isReportRefreshing(q: ReportQueryLike): boolean {
  return !!q.isSuccess && !!q.isFetching;
}
